"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { countSyllables } from "@/lib/tool-kit/style-checkers/syllables";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/* ------------------------------------------------------------------ */
/*  Regex constants                                                   */
/* ------------------------------------------------------------------ */

/*
 * Letters, syllables and sentence ends by Unicode property, not by `a-z`.
 *
 * `[^a-zA-Z]` was stripped from every word before its length was counted, so the
 * letters-per-word figure that Coleman-Liau and the Automated Readability Index are
 * built on was ZERO for Russian, Arabic, Hindi, Korean, Chinese and Japanese — both
 * indices reported grade 0 for any text in them, and "Avg Word Length" read 0.0.
 * Accented Latin was corrupted rather than emptied, which is worse because it looks like
 * it worked: "Grüße aus Köln. Das ist ein zweiter Satz." measured 29 letters instead of
 * 32, and "Đây là tiêu đề…" 14 instead of 25.
 *
 * `\p{M}` sits alongside `\p{L}` here because Devanagari writes its vowels as combining
 * marks: dropping them would shorten every Hindi word, and what these formulas want is
 * written length. (The `character-counter` widget deliberately does NOT include `\p{M}`
 * in its letter count — it answers a different question, "how many letters", where a
 * combining acute is not one.)
 *
 * `[.!?]` missed the sentence terminators of three of the site's scripts, so a Hindi or
 * Arabic document was one enormous sentence and every score came back red. `؟` `।` `॥`
 * `。` `！` `？` never occur in English prose, so adding them cannot move an English
 * score. `…` is deliberately NOT in the list: it does occur in English, and adding it
 * would split "Wait… what?" into two sentences.
 */
const RE_NON_LETTER = /[^\p{L}\p{M}]/gu;
const RE_COMBINING_MARK = /\p{M}/gu;
const RE_SPLIT_WORDS = /\s+/;
const RE_SPLIT_SENTENCES = /[.!?。！？؟।॥]+/;

/*
 * The vowel-group heuristic, made to work outside ASCII without growing a table.
 *
 * Enumerating every accented Latin vowel would mean ~90 code points (Vietnamese alone
 * has 60), so the word is decomposed to NFD and its combining marks dropped first: `ế`
 * becomes `e`, `ü` becomes `u`, `ą` becomes `a`, and one short class then covers every
 * Latin locale the site publishes. The four letters that do NOT decompose — `ı æ œ ø` —
 * are listed, plus the Cyrillic vowels, which makes Russian counts genuinely correct
 * (Cyrillic `й` decomposes to `и`, but it only ever follows a vowel, so it merges into
 * that vowel's group rather than adding one).
 *
 * Hangul is handled separately and exactly: a precomposed block is one syllable, so
 * Korean gets a real count rather than a fallback. Devanagari, Arabic and the CJK
 * scripts have no equivalent — Devanagari's inherent vowel is unwritten, Arabic's short
 * vowels usually are too — so words in them still count as one syllable each. That is
 * the same number they produced before this change; it is a stated limit, not a fix.
 * Latin-script words never reach this heuristic: they go to the shared English counter,
 * which knows silent e, -ed, -es and the vowel pairs that split.
 */
const RE_VOWEL_GROUPS = /[aeiouyıæœøаеиоуыэюяіїє]+/gu;
const RE_HANGUL_SYLLABLE = /[가-힣]/gu;

/** English strings. A translated content file overrides these through `ui`. */
export const READABILITY_CHECKER_STRINGS = {
  textareaAriaLabel: "Text to check readability",
  placeholder:
    "Paste or type your text here (at least a few sentences for accurate results)...",
  words: "Words",
  sentences: "Sentences",
  avgSentenceLength: "Avg Sentence Length",
  avgWordLength: "Avg Word Length",
  syllables: "Syllables",
  complexWords: "Complex Words (3+ syl)",
  readabilityScores: "Readability Scores",
  fleschReadingEaseName: "Flesch Reading Ease",
  fleschReadingEaseGreen: "Easy to read. Understood by 7th graders.",
  fleschReadingEaseYellow:
    "Fairly difficult. Best understood by high school students.",
  fleschReadingEaseRed: "Difficult. Best understood by college graduates.",
  fleschKincaidGradeName: "Flesch-Kincaid Grade",
  gradeText: "Grade {grade} - {description}",
  fleschKincaidGreen: "Easily understood by 13-14 year olds.",
  fleschKincaidYellow: "High school reading level.",
  fleschKincaidRed: "College-level reading required.",
  gunningFogName: "Gunning Fog Index",
  gunningFogGreen: "Easy to read for a wide audience.",
  gunningFogYellow: "Acceptable for most readers.",
  gunningFogRed: "Hard to read. Simplify your sentences.",
  colemanLiauName: "Coleman-Liau Index",
  colemanLiauGreen: "Middle school reading level.",
  colemanLiauYellow: "High school reading level.",
  colemanLiauRed: "College-level reading required.",
  smogIndexName: "SMOG Index",
  smogGreen: "Easily understood by the general public.",
  smogYellow: "Requires a high school education to understand.",
  smogRed: "Requires advanced education to understand.",
  automatedReadabilityName: "Automated Readability Index",
  ariGreen: "Suitable for middle school students.",
  ariYellow: "High school reading level.",
  ariRed: "College-level difficulty.",
} as const;

type Strings = typeof READABILITY_CHECKER_STRINGS;

/* ------------------------------------------------------------------ */
/*  Syllable counting                                                 */
/* ------------------------------------------------------------------ */

const RE_LATIN_ONLY = /^[\p{Script=Latin}\p{M}'’-]*$/u;

/**
 * Syllables in one word. Latin-script words go to the shared English counter (the one
 * the Syllable Counter and Reading Time Calculator use, so the three pages agree on
 * "jumped" and "create"); Hangul is counted exactly; other scripts fall back to vowel
 * groups, which is a stated limit and not an English rule.
 */
function countWordSyllables(word: string): number {
  const w = word.toLowerCase().replace(RE_NON_LETTER, "");

  // Hangul first: every precomposed block is exactly one syllable, and NFD below would
  // decompose them into jamo and lose the count.
  const hangul = w.match(RE_HANGUL_SYLLABLE);
  if (hangul) {
    return hangul.length;
  }

  if (RE_LATIN_ONLY.test(w)) {
    return Math.max(countSyllables(w), 1);
  }

  const bare = w.normalize("NFD").replace(RE_COMBINING_MARK, "");
  if (bare.length <= 2) {
    return 1;
  }
  const vowelGroups = bare.match(RE_VOWEL_GROUPS);
  return Math.max(vowelGroups ? vowelGroups.length : 1, 1);
}

/* ------------------------------------------------------------------ */
/*  Text analysis                                                     */
/* ------------------------------------------------------------------ */

export interface AnalysisResult {
  words: number;
  sentences: number;
  syllables: number;
  complexWords: number;
  polysyllables: number;
  characters: number;
  avgSentenceLen: number;
  avgWordLen: number;
}

export function analyzeText(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const wordsArray = trimmed.split(RE_SPLIT_WORDS).filter(Boolean);
  const words = wordsArray.length;
  const sentencesArray = trimmed
    .split(RE_SPLIT_SENTENCES)
    .filter((s) => s.trim().length > 0);
  const sentences = Math.max(sentencesArray.length, 1);

  let syllables = 0;
  let complexWords = 0;
  let polysyllables = 0;
  let characters = 0;

  for (const word of wordsArray) {
    const clean = word.replace(RE_NON_LETTER, "");
    characters += clean.length;
    const syl = countWordSyllables(clean);
    syllables += syl;
    if (syl >= 3) {
      complexWords++;
      polysyllables++;
    }
  }

  return {
    words,
    sentences,
    syllables,
    complexWords,
    polysyllables,
    characters,
    avgSentenceLen: words / sentences,
    avgWordLen: words > 0 ? characters / words : 0,
  };
}

/* ------------------------------------------------------------------ */
/*  Score calculations                                                */
/* ------------------------------------------------------------------ */

interface ScoreResult {
  name: string;
  value: number;
  display: string;
  interpretation: string;
  color: "green" | "yellow" | "red";
}

function gradeText(strings: Strings, grade: number, suffix: string): string {
  return t(strings.gradeText, {
    grade: Math.round(grade),
    description: suffix,
  });
}

function fleschReadingEase(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const score =
    206.835 - 1.015 * (a.words / a.sentences) - 84.6 * (a.syllables / a.words);
  const clamped = Math.max(0, Math.min(100, score));
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (clamped >= 70) {
    interpretation = strings.fleschReadingEaseGreen;
    color = "green";
  } else if (clamped >= 50) {
    interpretation = strings.fleschReadingEaseYellow;
    color = "yellow";
  } else {
    interpretation = strings.fleschReadingEaseRed;
    color = "red";
  }

  return {
    name: strings.fleschReadingEaseName,
    value: clamped,
    display: format(clamped, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

function fleschKincaidGrade(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const score =
    0.39 * (a.words / a.sentences) + 11.8 * (a.syllables / a.words) - 15.59;
  const clamped = Math.max(0, score);
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (clamped <= 8) {
    interpretation = gradeText(strings, clamped, strings.fleschKincaidGreen);
    color = "green";
  } else if (clamped <= 12) {
    interpretation = gradeText(strings, clamped, strings.fleschKincaidYellow);
    color = "yellow";
  } else {
    interpretation = gradeText(strings, clamped, strings.fleschKincaidRed);
    color = "red";
  }

  return {
    name: strings.fleschKincaidGradeName,
    value: clamped,
    display: format(clamped, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

function gunningFog(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const score =
    0.4 * (a.words / a.sentences + 100 * (a.complexWords / a.words));
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (score <= 8) {
    interpretation = strings.gunningFogGreen;
    color = "green";
  } else if (score <= 12) {
    interpretation = strings.gunningFogYellow;
    color = "yellow";
  } else {
    interpretation = strings.gunningFogRed;
    color = "red";
  }

  return {
    name: strings.gunningFogName,
    value: score,
    display: format(score, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

function colemanLiau(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const L = (a.characters / a.words) * 100;
  const S = (a.sentences / a.words) * 100;
  const score = 0.0588 * L - 0.296 * S - 15.8;
  const clamped = Math.max(0, score);
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (clamped <= 8) {
    interpretation = gradeText(strings, clamped, strings.colemanLiauGreen);
    color = "green";
  } else if (clamped <= 12) {
    interpretation = gradeText(strings, clamped, strings.colemanLiauYellow);
    color = "yellow";
  } else {
    interpretation = gradeText(strings, clamped, strings.colemanLiauRed);
    color = "red";
  }

  return {
    name: strings.colemanLiauName,
    value: clamped,
    display: format(clamped, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

function smogIndex(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const score = 3 + Math.sqrt(a.polysyllables * (30 / a.sentences));
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (score <= 8) {
    interpretation = strings.smogGreen;
    color = "green";
  } else if (score <= 12) {
    interpretation = strings.smogYellow;
    color = "yellow";
  } else {
    interpretation = strings.smogRed;
    color = "red";
  }

  return {
    name: strings.smogIndexName,
    value: score,
    display: format(score, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

function automatedReadability(
  a: AnalysisResult,
  strings: Strings,
  format: NumberFormatter
): ScoreResult {
  const score =
    4.71 * (a.characters / a.words) + 0.5 * (a.words / a.sentences) - 21.43;
  const clamped = Math.max(0, score);
  let interpretation: string;
  let color: "green" | "yellow" | "red";

  if (clamped <= 8) {
    interpretation = gradeText(strings, clamped, strings.ariGreen);
    color = "green";
  } else if (clamped <= 12) {
    interpretation = gradeText(strings, clamped, strings.ariYellow);
    color = "yellow";
  } else {
    interpretation = gradeText(strings, clamped, strings.ariRed);
    color = "red";
  }

  return {
    name: strings.automatedReadabilityName,
    value: clamped,
    display: format(clamped, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
    interpretation,
    color,
  };
}

/* ------------------------------------------------------------------ */
/*  Badge Component                                                   */
/* ------------------------------------------------------------------ */

const COLOR_CLASSES = {
  green: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  yellow:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
} as const;

function ScoreCard({ result }: { result: ScoreResult }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-medium text-foreground text-sm">{result.name}</h3>
        <span
          className={`rounded-full px-2.5 py-0.5 font-medium text-xs ${COLOR_CLASSES[result.color]}`}
        >
          {result.display}
        </span>
      </div>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {result.interpretation}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function ReadabilityCheckerWidget() {
  const strings = useToolUi(READABILITY_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");

  const analysis = analyzeText(text);

  const scores: ScoreResult[] = analysis
    ? [
        fleschReadingEase(analysis, strings, fmt),
        fleschKincaidGrade(analysis, strings, fmt),
        gunningFog(analysis, strings, fmt),
        colemanLiau(analysis, strings, fmt),
        smogIndex(analysis, strings, fmt),
        automatedReadability(analysis, strings, fmt),
      ]
    : [];

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textareaAriaLabel}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.placeholder}
        value={text}
      />

      {/* Text Stats Summary */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.words)}
            </p>
            <p className="text-muted-foreground text-xs">{strings.words}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.sentences)}
            </p>
            <p className="text-muted-foreground text-xs">{strings.sentences}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.avgSentenceLen, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </p>
            <p className="text-muted-foreground text-xs">
              {strings.avgSentenceLength}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.avgWordLen, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </p>
            <p className="text-muted-foreground text-xs">
              {strings.avgWordLength}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.syllables)}
            </p>
            <p className="text-muted-foreground text-xs">{strings.syllables}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="font-semibold text-foreground text-lg">
              {fmt(analysis.complexWords)}
            </p>
            <p className="text-muted-foreground text-xs">
              {strings.complexWords}
            </p>
          </div>
        </div>
      )}

      {/* Score Cards */}
      {scores.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.readabilityScores}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {scores.map((s) => (
              <ScoreCard key={s.name} result={s} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}
